// Desglose del precio de una guia: base, descuento de Beta, cupon y total.
//
// VIVA-1254. Lo que se pinta en la vista de contratacion, no lo que se cobra:
// el importe que llega a la pasarela lo calcula el servidor (VIVA-1250), piso
// incluido. Si los dos numeros difieren manda el del servidor y la vista tiene
// un defecto, no al reves.
//
// Toda la aritmetica vive AQUI, en funciones puras, por dos motivos: se puede
// probar sin montar la pantalla, y el dia que el contrato exista solo cambia de
// donde vienen las entradas.
//
// LA REGLA QUE NO SE PUEDE INVERTIR (epica VIVA-1148): el cupon se aplica sobre
// el precio YA REBAJADO por Beta, nunca sobre el base. Con base 1000, Beta 70%
// y cupon 20% el comprador paga 240, no 100. Invertido el orden el numero se ve
// plausible y esta mal, que es justo lo que lo hace peligroso.

// Piso de cobro, definitivo desde la decision del 09/09/2026 en VIVA-1214.
// Sustituye la regla del 10% del precio base que traia VIVA-1215.
export const GUIDE_PRICE_FLOOR = 4.99;

export const GUIDE_CURRENCY = "USD";

// El dinero se redondea a dos decimales en cada paso, no solo al final: es lo
// que hace que el desglose sume exactamente lo que dice el total.
const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? round2(amount) : 0;
};

// Un porcentaje solo cuenta si esta entre 0 y 100. Cualquier otra cosa —null,
// texto, negativo, 150— se trata como "sin descuento" en vez de producir un
// total absurdo.
const toPercent = (value) => {
  const percent = Number(value);
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  return percent > 100 ? 100 : percent;
};

// `betaDiscountPercent` y `couponPercent` todavia no llegan de ningun lado: el
// catalogo no devuelve el estado Beta y el contrato de cupones es VIVA-1215. Se
// aceptan como entradas opcionales para que conectarlos sea pasar un numero mas,
// y mientras tanto el desglose se reduce al precio publicado.
export const buildGuidePriceBreakdown = ({
  basePrice,
  betaDiscountPercent,
  couponPercent,
  currencyCode = GUIDE_CURRENCY,
} = {}) => {
  const base = toAmount(basePrice);
  const betaPercent = toPercent(betaDiscountPercent);
  const coupon = toPercent(couponPercent);

  const betaDiscount = round2((base * betaPercent) / 100);
  const afterBeta = round2(base - betaDiscount);

  // Sobre el precio ya rebajado. Este orden es el criterio, no una preferencia.
  const couponDiscount = round2((afterBeta * coupon) / 100);
  const afterCoupon = round2(afterBeta - couponDiscount);

  const hasDiscount = betaDiscount > 0 || couponDiscount > 0;

  const currency = currencyCode || GUIDE_CURRENCY;

  // CA-07: "dado cualquier CALCULO DE DESCUENTOS, cuando el resultado quede por
  // debajo de 4.99 USD, se cobra el piso". Se aplica solo cuando hubo descuento:
  // subir a 4.99 una guia publicada en 0.10, donde nadie descontó nada, seria
  // cambiarle el precio al autor, no proteger un minimo. Queda declarado aqui
  // porque es una lectura del criterio, no un hecho; el importe real lo resuelve
  // el servidor (VIVA-1250) y es el que manda.
  //
  // Y SOLO en dolares. El piso esta definido en USD, y el catalogo devuelve hoy
  // guias de pago en MXN (comprobado el 18/09/2026: las guias 28 y 29 traen
  // currencyCode "MXN" con precio 9.99 y 5). Comparar 5 MXN contra 4.99 USD y
  // "corregir" el total seria convertir monedas sin tipo de cambio: subiria el
  // precio casi veinte veces sin que nadie lo pidiera. Ante una moneda que no es
  // la del piso, el importe se respeta tal cual y se marca para que la pantalla
  // —y quien lea esto— sepa que ahi falta una decision.
  const floorApplies = currency === GUIDE_CURRENCY;
  const floorApplied =
    hasDiscount && floorApplies && afterCoupon < GUIDE_PRICE_FLOOR;
  const total = floorApplied ? GUIDE_PRICE_FLOOR : afterCoupon;

  return {
    // Bandera para quien pinte: la guia no esta en la moneda del cobro de guias,
    // que segun la decision del 09/09/2026 es el dolar SIEMPRE. El front no la
    // convierte; lo dice.
    currencyMismatch: !floorApplies,
    basePrice: base,
    betaDiscountPercent: betaPercent,
    betaDiscount,
    couponPercent: coupon,
    couponDiscount,
    total,
    floorApplied,
    hasDiscount,
    currencyCode: currency,
  };
};
